import { Component, Input, OnInit } from '@angular/core';
import { Unsubscriber } from '@core-lib/classes/unsubscriber.class';
import { ModalRef } from '@core-lib/modules/modal-container';
import Dynamsoft from 'dwt';
import { WebTwain } from 'dwt/dist/types/WebTwain';
import { IConfirmModalConfig } from '@core-lib/models';
import { DocumentGedService } from '@core-lib/modules/document-ged/services';
import { environment } from '@environments/environment';
import { jsPDF } from 'jspdf';
import { SharedStoreActions } from '@core-lib/store/shared-store';
import { Store } from '@ngrx/store';

@Component({
    selector: 'scanner',
    templateUrl: './scanner.component.html',
    styleUrls: ['./scanner.component.scss'],
    standalone: false
})
export class ScannerComponent extends Unsubscriber implements OnInit {
	private dynamsoftProductKey: string = environment.dynamsoftProductKey + ";" + environment.rasterizerDynamsoftProductKey;
	
	DWObject: WebTwain;
	selectSources: HTMLSelectElement;
	containerId = 'dwtcontrolContainer';
	deviceList = [];
	canSeeDoc = false;
	errorCodeScanner = '-1038';
	private _config: IConfirmModalConfig = { textsKey: 'ConfirmModal', modalOptions: { backdrop: 'static' } };

	@Input() set config(config: IConfirmModalConfig) {
		this._config = {
			cancelBtnText: 'Actions.Cancel',
			confirmBtnText: 'Actions.Confirm',
			canClose: true,
			canConfirm: true,
			...config
		};
	}

	@Input() scannerFileName: string;

	get config(): IConfirmModalConfig {
		return this._config;
	}

	get hasDocumentInBuffer(): boolean {
	return this.DWObject?.HowManyImagesInBuffer > 0;
	}

	/**
	 * Constructeur
	 * @param modalRef La référence de l'objet modal
	 */
	constructor(
		protected store$: Store,
		public modalRef: ModalRef,
		private documentGedService: DocumentGedService
	) {
		super();
	}

	ngOnInit(): void {
		Dynamsoft.DWT.Containers = [{ WebTwainId: 'dwtObject', ContainerId: this.containerId, Width: '100%', Height: '400px' }];
		Dynamsoft.DWT.RegisterEvent('OnWebTwainReady', () => {
			this.initDWT();
		});
		Dynamsoft.DWT.ResourcesPath = '/assets/dwt-resources';
		Dynamsoft.DWT.ProductKey = this.dynamsoftProductKey;
		Dynamsoft.DWT.Load();
	}

	/**
	 * Initialise le scanner
	 */
	initDWT(): void {
		this.DWObject = Dynamsoft.DWT.GetWebTwain(this.containerId);
		this.selectSources = <HTMLSelectElement>document.getElementById('sources');
		this.DWObject.GetDevicesAsync()
			.then(devices => {
				this.selectSources.options.length = 0;
				for (let i = 0; i < devices.length; i++) {
					// Get how many sources are installed in the system
					const opt = new Option(devices[i].displayName, i.toString());
      				opt.classList.add('scanner-option');
					this.selectSources.options.add(opt); // Add the sources in a drop-down list
					this.deviceList.push(devices[i]);
				}
			})
			.catch(function (exp) {
				alert(exp.message);
			});
	}

	acquireImage(): void {
		if (!this.DWObject) this.DWObject = Dynamsoft.DWT.GetWebTwain(this.containerId);
		if (this.selectSources.options.length > 0) {
			this.DWObject.SelectDeviceAsync(this.deviceList[this.selectSources.selectedIndex])
				.then(async () => {
					this.canSeeDoc = true;
					return this.DWObject.AcquireImageAsync({});
				})
				.then(() => {
					this.canSeeDoc = true;
					return this.DWObject.CloseSourceAsync();
				})
				.catch(exp => {
					if (exp.code == this.errorCodeScanner) {
						this.store$.dispatch(
							SharedStoreActions.OpenCancelConfirmationModal({
								textsKey: 'Common.Scanner.ErrorScanDocument',
								canConfirm: false,
								cancelBtnText: 'Actions.Close',
								modalOptions: {
									class: 'modal-md'
								}
							})
						);
					} else {
						this.store$.dispatch(
							SharedStoreActions.OpenCancelConfirmationModal({
								textsKey: 'Common.Scanner.ErrorScanDocument',
								canConfirm: false,
								cancelBtnText: 'Actions.Close',
								modalOptions: {
									class: 'modal-md'
								}
							})
						);
					}
				});
		} else {
			this.store$.dispatch(
				SharedStoreActions.OpenCancelConfirmationModal({
					textsKey: 'Common.Scanner.ErrorScanDocument',
					canConfirm: false
				})
			);
		}
	}

	async getDocumentFromScan() {
		// this.savePDFAndSend();
		if (this.DWObject.HowManyImagesInBuffer == 0) return;
		await this.processScannedImages();
		this.DWObject.RemoveAllImages();
		this.onClose();
	}

	async processScannedImages() {
		const count = this.DWObject.HowManyImagesInBuffer;
		if (count === 0) return;

		const pdf = new jsPDF({
			unit: 'mm',
  			format: 'a4'
		});

		for (let i = 0; i < count; i++) {
			const imageUrl = this.DWObject.GetImageURL(i);
			const blob = await this.fetchImageAsBlob(imageUrl);

			await this.appendBlobToPDF(blob, pdf);

			// Optionally add new pages for subsequent images
			if (i < count - 1) {
				pdf.addPage();
			}
		}

		this.documentGedService.addDocument(pdf.output('blob'), this.scannerFileName);
	}

	async appendBlobToPDF(blob: Blob, pdf: jsPDF): Promise<void> {
		// Set the dimensions for the image to match A4 size
		const imgWidth = 210; // A4 width in mm
		const imgHeight = 297; // A4 height in mm

		return new Promise((resolve, reject) => {
			const reader = new FileReader();

			reader.onloadend = () => {
				const dataUrl = reader.result as string;
				pdf.addImage(dataUrl, 'PNG', 0, 0, imgWidth, imgHeight); // Adjust dimensions as needed
				resolve();
			};

			reader.onerror = () => {
				reject(new Error('Failed to read blob'));
			};

			reader.readAsDataURL(blob);
		});
	}

	async fetchImageAsBlob(url: string): Promise<Blob> {
		const response = await fetch(url);
		const blob = await response.blob();
		return blob;
	}

	getImageIndices() {
		let indices = [];
		for (let index = 0; index < this.DWObject.HowManyImagesInBuffer; index++) {
			indices.push(index);
		}
		return indices;
	}

	/**
	 * Action fermeture popup: ANNULATION
	 */
	onClose(): void {
		this.modalRef.hide();
	}
}
